import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth }           from '@/contexts/AuthContext';
import FilterBar             from '@/components/FilterBar';
import type { FilterField }  from '@/components/FilterBar';
import PaginationBar         from '@/components/PaginationBar';
import SortableTh            from '@/components/SortableTh';
import ExportButton          from '@/components/ExportButton';
import type { ExportScope }  from '@/components/ExportButton';
import { useSort }           from '@/hooks/useSort';
import { exportData } from '@/utils/export';
import type { ExportFormat } from '@/utils/export';
import { listFamilies, deleteFamily } from '@/api/family';
import type { FamilyListItem, SubPendingStatus } from '@/api/family';
import { listAnbiyam } from '@/api/anbiyam';
import type { Anbiyam } from '@/api/anbiyam';

const DEFAULT_FILTERS = {
  familyCode: null as null | string,
  head:       null as null | string,
  occupation: null as null | string,
  anbiyamId:  null as null | number,
  status:     null as null | 'active' | 'inactive',
  subStatus:  null as null | SubPendingStatus,
};

/**
 * sp_GetFamilyListPaged returns a DataTable bound directly to the WinForms
 * grid (no fixed column-name mapping in source) — column names may vary slightly
 * by deployment. This helper tries the likely keys in order and falls back gracefully.
 */
const pick = (row: FamilyListItem, keys: string[]): string => {
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && v !== '') return String(v);
  }
  return '—';
};

export default function FamilyPage() {
  const { isAdmin } = useAuth();
  const navigate    = useNavigate();

  const [rows,       setRows]       = useState<FamilyListItem[]>([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [fetchErr,   setFetchErr]   = useState('');

  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // ?status=inactive deep-link (from the Dashboard's Inactive Families card)
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status');
  const [filters, setFilters] = useState<typeof DEFAULT_FILTERS>({
    ...DEFAULT_FILTERS,
    status: urlStatus === 'active' || urlStatus === 'inactive' ? urlStatus : null,
  });

  const [anbiyams, setAnbiyams] = useState<Anbiyam[]>([]);
  useEffect(() => {
    listAnbiyam({ pageSize: 200 }).then((r) => setAnbiyams(r.items)).catch(() => {});
  }, []);

  // Build filter fields dynamically so anbiyam dropdown is populated from API
  const filterFields: FilterField[] = [
    { key: 'familyCode', label: 'Family Code', type: 'text',  placeholder: 'e.g. YA12' },
    { key: 'head',       label: 'Head Name',  type: 'text',   placeholder: 'Search by name…' },
    { key: 'occupation', label: 'Occupation', type: 'text',   placeholder: 'Search by occupation…' },
    {
      key:     'anbiyamId',
      label:   'Anbiyam',
      type:    'select',
      options: anbiyams.map((a) => ({ label: a.anbiyamName, value: a.anbiyamId })),
    },
    {
      key:     'status',
      label:   'Status',
      type:    'select',
      options: [{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }],
    },
    {
      key:     'subStatus',
      label:   'Sub. Pending',
      type:    'select',
      options: [
        { label: 'Paid up', value: 'Paid up' },
        { label: 'Pending (1-2 months)', value: 'Pending' },
        { label: 'Long Pending (3+ months)', value: 'Long Pending' },
      ],
    },
  ];

  const [deleteTarget, setDeleteTarget] = useState<FamilyListItem | null>(null);
  const [deleteErr,    setDeleteErr]    = useState('');
  const [deleting,     setDeleting]     = useState(false);

  const handleExport = async (format: ExportFormat, scope: ExportScope) => {
    const result = await listFamilies({
      page: 1, pageSize: 9999,
      head:       scope === 'filtered' ? (filters.head       ?? undefined) : undefined,
      occupation: scope === 'filtered' ? (filters.occupation ?? undefined) : undefined,
      anbiyamId:  scope === 'filtered' ? (filters.anbiyamId  ?? undefined) : undefined,
      status:     scope === 'filtered' ? (filters.status     ?? undefined) : undefined,
      familyCode: scope === 'filtered' ? (filters.familyCode ?? undefined) : undefined,
      subStatus:  scope === 'filtered' ? (filters.subStatus  ?? undefined) : undefined,
    });
    exportData({
      title:    'Family List',
      filename: 'families-export',
      columns: [
        { label: 'Family Code',    key: 'familyCode',  identity: true },
        { label: 'Head of Family', key: 'familyHead',  identity: true },
        { label: 'Anbiyam',        key: 'anbiyamName', identity: true },
        { label: 'Phone',          key: 'phone'      },
        { label: 'Occupation',     key: 'headOccupation' },
        { label: 'Cemetery Plots', key: 'cemeteryCount' },
        { label: 'Sub. Pending',   key: 'subPendingStatus' },
        { label: 'Subscription',   key: 'subscription' },
      ],
      rows: result.items as unknown as Record<string, unknown>[],
      filters: {
        'Head Name':  filters.head ?? '',
        'Occupation': filters.occupation ?? '',
        'Anbiyam':    anbiyams.find((a) => a.anbiyamId === filters.anbiyamId)?.anbiyamName ?? '',
        'Status':     filters.status ?? '',
        'Family Code': filters.familyCode ?? '',
        'Sub. Pending': filters.subStatus ?? '',
      },
    }, format);
  };

  const { sorted, sort, toggleSort } = useSort(rows as unknown as Record<string, unknown>[]);
  const displayRows = sorted as unknown as FamilyListItem[];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchErr('');
    try {
      const result = await listFamilies({
        page,
        pageSize,
        head:       filters.head       ?? undefined,
        occupation: filters.occupation ?? undefined,
        anbiyamId:  filters.anbiyamId  ?? undefined,
        status:     filters.status     ?? undefined,
        familyCode: filters.familyCode ?? undefined,
        subStatus:  filters.subStatus  ?? undefined,
      });
      setRows(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch {
      setFetchErr('Failed to load families. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterChange = (key: string, value: string | number | null) => {
    if (key === 'anbiyamId') {
      setFilters((prev) => ({ ...prev, anbiyamId: value ? Number(value) : null }));
    } else {
      setFilters((prev) => ({ ...prev, [key]: value as string | null }));
    }
    setPage(1);
  };

  const handleReset = () => { setFilters(DEFAULT_FILTERS); setPage(1); };
  const handlePageSize = (s: number) => { setPageSize(s); setPage(1); };

  const confirmDelete = (row: FamilyListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(row);
    setDeleteErr('');
  };
  const cancelDelete = () => { setDeleteTarget(null); setDeleteErr(''); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteErr('');
    try {
      await deleteFamily(deleteTarget.familyId);
      setDeleteTarget(null);
      fetchData();
    } catch (err: any) {
      setDeleteErr(err?.response?.data?.error ?? 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  // 1 serial + 7 data cols + 1 actions (admin only)
  const COLS = isAdmin ? 9 : 8;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-navy font-georgia">Families</h1>
          <p className="text-xs text-teal mt-0.5">Browse and manage registered families</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButton
            onExport={handleExport}
            hasFilters={!!(filters.head || filters.occupation || filters.anbiyamId || filters.status || filters.familyCode || filters.subStatus)}
          />
          {isAdmin && (
            <button onClick={() => navigate('/families/new')} className="btn-primary">
              + Add Family
            </button>
          )}
        </div>
      </div>

      <FilterBar
        fields={filterFields}
        values={filters}
        onChange={handleFilterChange}
        onReset={handleReset}
      />

      {fetchErr && (
        <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded px-4 py-2 mb-4">
          {fetchErr}
        </div>
      )}

      <div className="hidden sm:block bg-white rounded-lg border border-grid-border shadow-sm overflow-x-auto">
        <table className="w-full text-sm text-navy font-georgia">
          <thead>
            <tr className="bg-navy text-offwhite text-left text-xs uppercase tracking-wide">
              <th className="px-4 py-3">#</th>
              <SortableTh label="Family Code"    sortKey="familyCode"     sort={sort} onSort={toggleSort} />
              <SortableTh label="Head of Family" sortKey="familyHead"     sort={sort} onSort={toggleSort} />
              <SortableTh label="Anbiyam"        sortKey="Anbiyam"        sort={sort} onSort={toggleSort} />
              <SortableTh label="Phone"          sortKey="phone"          sort={sort} onSort={toggleSort} />
              <SortableTh label="Occupation"     sortKey="headOccupation" sort={sort} onSort={toggleSort} />
              <SortableTh label="Cemetery"       sortKey="cemeteryCount"  sort={sort} onSort={toggleSort} />
              <SortableTh label="Sub. Pending"   sortKey="subPendingPct"  sort={sort} onSort={toggleSort} />
              {isAdmin && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLS} className="text-center py-10 text-teal text-xs">
                  Loading…
                </td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td colSpan={COLS} className="text-center py-10 text-gray-400 text-xs">
                  No families found.
                </td>
              </tr>
            ) : (
              displayRows.map((row, idx) => {
                const inactive = row.isActive === false;
                // An inactive family has left — "Long Pending" dues would be misleading.
                const pending = inactive ? null : row.subPendingStatus as string | null | undefined;
                const pendingCls =
                  pending == null          ? 'text-gray-300'
                  : pending === 'Paid up'  ? 'text-green-600 font-medium'
                  : pending === 'Pending'  ? 'text-amber-600 font-medium'
                  : 'text-red-500 font-medium';

                return (
                  <tr
                    key={row.familyId}
                    onClick={() => navigate(`/families/${row.familyId}`)}
                    className={`cursor-pointer hover:bg-teal/5 ${idx % 2 === 0 ? 'bg-white' : 'bg-offwhite/50'}`}
                  >
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {(page - 1) * pageSize + idx + 1}
                    </td>
                    <td className="px-4 py-3 font-medium text-teal whitespace-nowrap">
                      {pick(row, ['familyCode', 'FamilyCode'])}
                      {inactive && <span className="ml-1.5 text-xs font-normal text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded">Inactive</span>}
                    </td>
                    <td className="px-4 py-3">
                      {pick(row, ['familyHead', 'FamilyHead', 'headOfFamily', 'HeadOfFamily'])}
                    </td>
                    <td className="px-4 py-3">
                      {pick(row, ['anbiyamName', 'AnbiyamName', 'anbiyam', 'Anbiyam'])}
                    </td>
                    <td className="px-4 py-3">
                      {pick(row, ['phone', 'Phone', 'familyPhone', 'FamilyPhone'])}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {(row.headOccupation as string | null | undefined) || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(row.cemeteryCount as number) > 0 ? (
                        <span className="inline-block bg-teal/10 text-teal text-xs font-medium px-2 py-0.5 rounded-full">
                          {row.cemeteryCount as number}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-center text-xs ${pendingCls}`}>
                      {pending ?? '—'}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/families/${row.familyId}/edit`); }}
                            className="btn-secondary !px-2 !py-1 text-xs"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => confirmDelete(row, e)}
                            className="btn-danger !px-2 !py-1 text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Compact cards on phones */}
      <div className="sm:hidden space-y-3">
        {loading ? (
          <p className="text-center py-10 text-teal text-xs">Loading…</p>
        ) : displayRows.length === 0 ? (
          <p className="text-center py-10 text-gray-400 text-xs">No families found.</p>
        ) : (
          displayRows.map((row) => {
            const inactive = row.isActive === false;
            const pending = inactive ? null : row.subPendingStatus as string | null | undefined;
            const pillCls =
              pending == null         ? 'bg-offwhite text-gray-400 border-grid-border'
              : pending === 'Paid up' ? 'bg-green-50 text-green-700 border-green-200'
              : pending === 'Pending' ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-red-50 text-red-600 border-red-200';
            const pillLabel = pending ?? '—';
            return (
              <div
                key={row.familyId}
                onClick={() => navigate(`/families/${row.familyId}`)}
                className="bg-white rounded-lg border border-grid-border shadow-sm p-3 cursor-pointer active:bg-offwhite/60"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-navy font-georgia truncate">
                      {pick(row, ['familyHead', 'FamilyHead', 'headOfFamily', 'HeadOfFamily'])}
                    </p>
                    <p className="text-xs text-teal font-georgia">
                      {pick(row, ['familyCode', 'FamilyCode'])}
                      {inactive && <span className="ml-1.5 text-xs font-normal text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded">Inactive</span>}
                    </p>
                  </div>
                  <span className={`flex-shrink-0 text-xs font-bold border rounded-full px-2 py-0.5 whitespace-nowrap ${pillCls}`}>
                    {pillLabel}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className="text-xs text-navy bg-offwhite border border-grid-border rounded px-2 py-0.5">
                    {pick(row, ['anbiyamName', 'AnbiyamName', 'anbiyam', 'Anbiyam'])}
                  </span>
                  <span className="text-xs text-navy bg-offwhite border border-grid-border rounded px-2 py-0.5">
                    {pick(row, ['phone', 'Phone', 'familyPhone', 'FamilyPhone'])}
                  </span>
                  {(row.headOccupation as string | null | undefined) && (
                    <span className="text-xs text-navy bg-offwhite border border-grid-border rounded px-2 py-0.5">
                      {row.headOccupation as string}
                    </span>
                  )}
                  {(row.cemeteryCount as number) > 0 && (
                    <span className="text-xs text-navy bg-offwhite border border-grid-border rounded px-2 py-0.5">
                      {row.cemeteryCount as number} cemetery plot{(row.cemeteryCount as number) > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/families/${row.familyId}/edit`); }}
                      className="flex-1 btn-secondary !py-1.5 text-xs"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => confirmDelete(row, e)}
                      className="flex-1 btn-danger !py-1.5 text-xs"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <PaginationBar
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPage={setPage}
        onPageSize={handlePageSize}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-base font-semibold text-navy font-georgia mb-2">
              Delete Family
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete family{' '}
              <span className="font-semibold">{pick(deleteTarget, ['familyCode', 'FamilyCode'])}</span>?
              This cannot be undone.
            </p>
            {deleteErr && <p className="text-red-600 text-sm mb-3">{deleteErr}</p>}
            <div className="flex justify-end gap-3">
              <button onClick={cancelDelete} className="btn-secondary" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleDelete} className="btn-danger" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
